import type { LayerData } from './Layer.types';

// Building blocks for recognising a "Password: <value>" line in a text layer's content.
// - label: optional leading spaces, "password" (any case), a colon, optional spaces.
// - value: any non-whitespace run, so special characters in a password are handled too (a password
//   is a single whitespace-free token). Using `\S*` rather than `[A-Za-z0-9]*` avoids the line
//   silently stopping to match once a special-character password is synced in.
const LABEL = '[^\\S\\n]*password[^\\S\\n]*:[^\\S\\n]*';
const VALUE = '\\S*';

// Per-line matcher (m + g): swaps the value wherever a "Password: <value>" line appears, even inside
// a larger block. Captures the label (case/spacing preserved), the value, and trailing whitespace.
const PASSWORD_LINE_REPLACE = new RegExp(`^(${LABEL})(${VALUE})([^\\S\\n]*)$`, 'gim');

// Whole-content matcher: the content is *exactly* one password line (ignoring surrounding
// whitespace). Deliberately NOT multiline — a multiline block that merely contains a password line
// is not treated as one, so editing the rest of that prose stays possible.
const PASSWORD_ONLY_TEST = new RegExp(`^${LABEL}${VALUE}$`, 'i');

/**
 * Client-side stamp (saved with the draft, like the basicDigitalV2 stamp — no template change) put
 * on the template's password text layer once we recognise it. Keeping the stamp lets us hide the
 * line entirely (clear its content) for a custom link or public event and restore it later, since a
 * cleared layer no longer matches the content heuristic.
 */
export const PASSWORD_LAYER_NAME = 'PASSWORD';

/** True when a text layer's content is *only* a "Password: <value>" display line. */
export const isPasswordDisplayContent = (content: string | null | undefined): boolean => !!content && PASSWORD_ONLY_TEST.test(content.trim());

/**
 * True when a layer is the password display layer — either already stamped, or a text layer whose
 * content is a password line (not yet stamped). Its value is derived from the event password, so it
 * must not be inline-edited.
 */
export const isPasswordDisplayLayer = (layer: LayerData | undefined): boolean =>
  layer?.type === 'text' && (layer.name === PASSWORD_LAYER_NAME || isPasswordDisplayContent(layer.textData.content));

/** The visible password line for a given password (canonical label, used when restoring a hidden layer). */
export const buildPasswordLine = (eventPassword: string): string => `Password: ${eventPassword}`;

/** Swap the value in every "Password: <value>" line for the given password, preserving the label and spacing. */
export const syncPasswordDisplayContent = (content: string, eventPassword: string): string =>
  content.replace(PASSWORD_LINE_REPLACE, (_match, label, _oldValue, trailing) => `${label}${eventPassword}${trailing}`);
